import React, { SelectHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options?: { value: string; label: string }[];
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, label, error, options, fullWidth = false, id, children, ...props },
    ref
  ) => {
    const selectId = id || React.useId();

    return (
      <div className={cn('space-y-1', fullWidth ? 'w-full' : 'w-fit')}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'block w-full px-2 py-2 bg-white border-2 rounded-lg',
              'text-gray-900 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-200 appearance-none',
              'min-h-[36px]',
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-orange-200 hover:border-orange-300 focus:border-orange-500',
              className,
              // Dark mode classes
              'dark:bg-slate-700 dark:text-slate-300'
            )}
            {...props}
          >
            {options ? (
              options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-white text-gray-900 font-semibold py-2 text-base"
                >
                  {option.label}
                </option>
              ))
            ) : (
              children
            )}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-orange-500">
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
